// LiveArbitrageExperiment.js — Main container for the live arbitrage experiment dashboard.
// 10 panels: header, countdown, auction, FX ticker, yield comparison, investor P&L,
// Forwardsflow P&L, ledger, assumptions drawer, methodology footer.

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import CountdownClock from './CountdownClock';
import AuctionResultsCard from './AuctionResultsCard';
import FxTicker from './FxTicker';
import YieldComparison from './YieldComparison';
import InvestorPLWaterfall from './InvestorPLWaterfall';
import ForwardsflowPL from './ForwardsflowPL';
import ExperimentLedger from './ExperimentLedger';
import AssumptionsDrawer from './AssumptionsDrawer';
import ReportsPanel from './ReportsPanel';
import { RefreshCw, Play, Download, FileText } from 'lucide-react';

const STATUS_COLORS = {
  pre_auction: 'bg-amber-100 text-amber-800',
  auction_day: 'bg-orange-100 text-orange-800',
  settled: 'bg-blue-100 text-blue-800',
  accruing: 'bg-green-100 text-green-800',
  matured: 'bg-emerald-100 text-emerald-800',
  rolled: 'bg-purple-100 text-purple-800',
  closed: 'bg-zinc-100 text-zinc-800',
  error: 'bg-red-100 text-red-800',
};

export default function LiveArbitrageExperiment() {
  const [experiment, setExperiment] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [marks, setMarks] = useState([]);
  const [reports, setReports] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [closeNowData, setCloseNowData] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);
  const [activePanel, setActivePanel] = useState('overview');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load experiments — take the first active one
      const expsRes = await api.getLiveExperiments();
      if (expsRes.success && expsRes.data.length > 0) {
        const exp = expsRes.data[0];
        const detailRes = await api.getLiveExperiment(exp.id);
        if (detailRes.success) {
          setExperiment(detailRes.data);
        }

        // Load marks
        const marksRes = await api.getLiveExperimentMarks(exp.id, { granularity: 'day' });
        if (marksRes.success) setMarks(marksRes.data);

        // Load reports
        const reportsRes = await api.getLiveExperimentReports(exp.id).catch(() => ({ success: false }));
        if (reportsRes.success) setReports(reportsRes.data);
      }

      // Load countdown
      const cdRes = await api.getCountdown().catch(() => ({ success: false }));
      if (cdRes.success) setCountdown(cdRes.data);

      // Load recipients (API returns objects, ReportsPanel expects email strings)
      const recipRes = await api.getReportRecipients().catch(() => ({ success: false }));
      if (recipRes.success) setRecipients((recipRes.data || []).map(r => typeof r === 'string' ? r : r.email));

      // Load audit log
      const auditRes = await api.get('/sim-admin/audit-log?action=assumptions_updated&limit=20').catch(() => ({ success: false }));
      if (auditRes.success) setAuditLog(auditRes.data);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleDryRun = async () => {
    if (!experiment) return;
    try {
      const res = await api.dryRunExperiment(experiment.id);
      if (res.success) {
        alert('Dry run completed. Check console for details.');
        console.log('Dry run result:', res.data);
        loadData();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCloseNow = async () => {
    if (!experiment) return;
    try {
      const res = await api.closeExperimentNow(experiment.id);
      if (res.success) setCloseNowData(res.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFetchResults = async () => {
    if (!experiment) return;
    try {
      await api.fetchResultsNow(experiment.id);
      setTimeout(loadData, 5000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExport = async () => {
    if (!experiment) return;
    try {
      const blob = await api.getLiveExperimentExport(experiment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `experiment-${experiment.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveAssumptions = async (newAssumptions) => {
    if (!experiment) return;
    try {
      await api.updateLiveExperimentAssumptions(experiment.id, newAssumptions);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePreviewReport = async () => {
    if (!experiment) return;
    try {
      const blob = await api.previewReport(experiment.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendReport = async () => {
    if (!experiment) return;
    try {
      await api.sendReportNow(experiment.id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateRecipients = async (newRecipients) => {
    // Convert email strings to {email, name} objects for the API
    const formatted = newRecipients.map(r => typeof r === 'string' ? { email: r } : r);
    await api.updateReportRecipients({ recipients: formatted });
    loadData();
  };

  const handleGetPresignedUrl = async (docId) => {
    try {
      const res = await api.getPresignedCbkDoc(docId);
      if (res.success) window.open(res.url, '_blank');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading && !experiment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh]">
        <div className="w-6 h-6 border-2 border-[#4D4D4D] border-t-transparent rounded-full animate-spin" />
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 mt-3">Loading experiment...</p>
      </div>
    );
  }

  if (!experiment) {
    const BID_CLOSE_AT = '2026-10-01T11:00:00Z'; // 14:00 EAT = 11:00 UTC
    return (
      <div className="space-y-6 max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="font-sans text-xl font-bold text-black">CHF → KES 364-day T-bill Arbitrage</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg mx-auto">
            Live simulation: CHF 1M investment into the Kenyan 364-day
            T-bill (Issue 2631/364), hedged with a Forwardsflow forward.
          </p>
        </div>

        {/* Countdown to bid close */}
        <CountdownClock
          bidCloseAt={BID_CLOSE_AT}
          settlementDate="2026-10-05"
          serverNow={new Date().toISOString()}
          status="pending_auction"
        />

        {/* Experiment parameters */}
        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto text-left">
          <div className="bg-zinc-50 border border-zinc-200 p-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Principal</p>
            <p className="font-mono text-sm font-bold text-black">CHF 1,000,000</p>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 p-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Instrument</p>
            <p className="font-mono text-sm font-bold text-black">KE T-bill 364d</p>
          </div>
          <div className="bg-zinc-50 border border-zinc-200 p-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">Target Issue</p>
            <p className="font-mono text-sm font-bold text-black">2631/364</p>
          </div>
        </div>

        {/* Launch button */}
        <div className="text-center">
          <button
            disabled={launching}
            onClick={async () => {
              try {
                setLaunching(true);
                setError(null);
                // Run migration first to ensure schema exists
                await api.runSimMigration('003_live_experiment.sql');
                await api.createLiveExperiment({
                  principal_chf: 1000000,
                  target_issue_no: '2631/364',
                  bid_close_at: BID_CLOSE_AT,
                });
                await loadData();
              } catch (err) {
                setError(err.message);
              } finally {
                setLaunching(false);
              }
            }}
            className="px-6 py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all inline-flex items-center gap-2 disabled:opacity-50"
          >
            {launching ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Launching...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Launch Experiment
              </>
            )}
          </button>
          {error && (
            <p className="text-red-600 text-sm mt-3">{error}</p>
          )}
        </div>
      </div>
    );
  }

  const latestMark = experiment.latestMark || (marks.length > 0 ? marks[marks.length - 1] : null);
  const assumptions = typeof experiment.assumptions_json === 'string'
    ? JSON.parse(experiment.assumptions_json) : experiment.assumptions_json;

  const panels = [
    { key: 'overview', label: 'Overview' },
    { key: 'investor', label: 'Investor P&L' },
    { key: 'forwardsflow', label: 'FF P&L' },
    { key: 'ledger', label: 'Ledger' },
    { key: 'reports', label: 'Reports' },
    { key: 'assumptions', label: 'Assumptions' },
  ];

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 text-sm border border-red-200">
          {error}
          <button onClick={() => setError(null)} className="float-right text-red-500 font-bold">×</button>
        </div>
      )}

      {/* Panel 1: Header Strip */}
      <div className="flex items-center justify-between border-b border-zinc-200 pb-4">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block">Live Arbitrage Experiment</span>
          <h2 className="font-sans text-xl font-bold text-black mt-1">{experiment.name}</h2>
          <div className="flex items-center gap-3 mt-2">
            <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider rounded-none ${STATUS_COLORS[experiment.status] || 'bg-zinc-100 text-zinc-800'}`}>
              {experiment.status}
            </span>
            <span className="font-mono text-xs text-zinc-500">CHF {Number(experiment.principal_chf).toLocaleString()}</span>
            <span className="font-mono text-xs text-zinc-500">Issue {experiment.target_issue_no}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadData}
            className="p-2 border border-zinc-300 hover:border-[#4D4D4D] transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={handleDryRun}
            className="flex items-center gap-1.5 px-3 py-2 border border-zinc-300 hover:border-[#4D4D4D] font-mono text-xs uppercase tracking-[0.15em]">
            <Play className="w-3 h-3" /> Dry Run
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 border border-zinc-300 hover:border-[#4D4D4D] font-mono text-xs uppercase tracking-[0.15em]">
            <Download className="w-3 h-3" /> Export
          </button>
          <button onClick={handlePreviewReport}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#4D4D4D] text-white border border-[#4D4D4D] hover:bg-black font-mono text-xs uppercase tracking-[0.15em]">
            <FileText className="w-3 h-3" /> Report
          </button>
        </div>
      </div>

      {/* Panel 2: Countdown Clock */}
      <CountdownClock
        bidCloseAt={countdown?.bid_close_at || experiment.bid_close_at}
        settlementDate={countdown?.settlement_date || experiment.settlement_date}
        serverNow={countdown?.server_now}
        status={experiment.status}
      />

      {/* Panel Navigation */}
      <div className="border-b border-zinc-200">
        <div className="flex gap-1">
          {panels.map(p => (
            <button key={p.key} onClick={() => setActivePanel(p.key)}
              className={`px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors border-b-2 ${
                activePanel === p.key
                  ? 'border-[#4D4D4D] text-black font-bold'
                  : 'border-transparent text-zinc-400 hover:text-zinc-600'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Panel Content */}
      {activePanel === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Panel 3: Auction Results */}
            <AuctionResultsCard
              auction={experiment.auction}
              docs={experiment.docs || []}
              onFetchResults={handleFetchResults}
              onGetPresignedUrl={handleGetPresignedUrl}
            />

            {/* Panel 4: FX Ticker */}
            <FxTicker
              entryFxRate={experiment.entry_fx_rate}
              lockedForwardRate={experiment.locked_forward_rate}
              latestMark={latestMark}
              marks={marks}
            />
          </div>

          {/* Panel 5: Yield Comparison */}
          <YieldComparison
            experiment={experiment}
            latestMark={latestMark}
            marks={marks}
          />
        </div>
      )}

      {activePanel === 'investor' && (
        /* Panel 6: Investor P&L Waterfall */
        <InvestorPLWaterfall
          experiment={experiment}
          latestMark={latestMark}
          closeNowData={closeNowData}
          onCloseNow={handleCloseNow}
        />
      )}

      {activePanel === 'forwardsflow' && (
        /* Panel 7: Forwardsflow P&L */
        <ForwardsflowPL
          latestMark={latestMark}
          events={experiment.events || []}
        />
      )}

      {activePanel === 'ledger' && (
        /* Panel 8: Ledger */
        <ExperimentLedger
          events={experiment.events || []}
          onExport={handleExport}
        />
      )}

      {activePanel === 'reports' && (
        /* Reports Panel */
        <ReportsPanel
          reports={reports}
          recipients={recipients}
          onSendNow={handleSendReport}
          onPreview={handlePreviewReport}
          onUpdateRecipients={handleUpdateRecipients}
        />
      )}

      {activePanel === 'assumptions' && (
        /* Panel 9: Assumptions Drawer */
        <AssumptionsDrawer
          assumptions={assumptions}
          onSave={handleSaveAssumptions}
          auditLog={auditLog}
        />
      )}

      {/* Panel 10: Methodology Footer */}
      <div className="bg-zinc-50 border border-zinc-200 p-6 mt-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 block mb-2">Methodology</span>
        <div className="font-mono text-xs text-zinc-600 space-y-1">
          <p>T-bill pricing: ACT/364 discount convention per CBK rules.</p>
          <p>Forward rate: Spot × (1 + premium × T/360). Premium locked at trade inception.</p>
          <p>Accrual: Straight-line actual/365 from settlement to maturity.</p>
          <p>WHT: Applied at source by CBK at maturity (15% non-resident rate).</p>
          <p>Investor P&L (CHF): Hedged = accrued KES ÷ locked forward. Unhedged = accrued KES ÷ spot.</p>
          <p>FF P&L (KES): FX conversion fee (realised) + settlement costs (accrued) + forward MTM.</p>
          <p>All marks computed via markToMarket engine function — never recomputed separately.</p>
        </div>
      </div>
    </div>
  );
}
